import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth';
import { useMercadoPagoContext } from '../context/MercadoPagoContext';

/**
 * Hook that automatically checks MercadoPago connection status ONCE when user logs in
 * and redirects to configuration page if not connected
 * This hook should be used in the main app component or layout
 */
export const useMercadoPagoAutoCheck = () => {
  const { isLoggedIn, user } = useAuth();
  const { checkConnectionStatus, isLoading, isConnected, error } = useMercadoPagoContext();
  const lastCheckedUserIdRef = useRef<string | number | null>(null);
  const isCheckingRef = useRef<boolean>(false);
  const hasCompletedCheckRef = useRef<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Check MercadoPago status ONCE per login OR on page load for existing sessions
  useEffect(() => {
    console.log('🔄 useMercadoPagoAutoCheck verification effect triggered:', {
      isLoggedIn,
      hasUser: !!user,
      userId: user?.id,
      lastCheckedUserId: lastCheckedUserIdRef.current,
      isNewUserOrFirstCheck: user ? (user.id !== lastCheckedUserIdRef.current || lastCheckedUserIdRef.current === null) : false,
      isCheckingInProgress: isCheckingRef.current
    });

    // Only check if:
    // 1. User is logged in
    // 2. We have user data
    // 3. This is a new user (different from last checked) OR we haven't checked for this session yet
    // 4. We're not already checking
    if (
      isLoggedIn && 
      user && 
      (user.id !== lastCheckedUserIdRef.current || lastCheckedUserIdRef.current === null) && 
      !isCheckingRef.current
    ) {
      console.log('🔄 Usuario logueado detectado, verificando MercadoPago...', {
        userId: user.id,
        isNewUser: user.id !== lastCheckedUserIdRef.current,
        isFirstCheck: lastCheckedUserIdRef.current === null
      });
      
      lastCheckedUserIdRef.current = user.id || null;
      isCheckingRef.current = true;
      hasCompletedCheckRef.current = false;
      
      checkConnectionStatus().finally(() => {
        // Mark as completed immediately when the request finishes
        console.log('🏁 Verificación de MercadoPago completada para usuario:', user.id);
        isCheckingRef.current = false;
        hasCompletedCheckRef.current = true;
      });
    }
  }, [isLoggedIn, user, checkConnectionStatus]);

  // Handle redirection ONCE after check is complete
  useEffect(() => {
    console.log('🔄 useMercadoPagoAutoCheck redirection effect:', {
      isLoggedIn,
      hasUser: !!user,
      userId: user?.id,
      isLoading,
      lastCheckedUserId: lastCheckedUserIdRef.current,
      hasCompletedCheck: hasCompletedCheckRef.current,
      isConnected,
      error: !!error,
      currentPath: location.pathname
    });

    // DISABLED: Automatic redirection to configuration when MercadoPago is not connected
    // This was making MercadoPago connection mandatory, but now it's optional
    // Users can choose their preferred payment method from the payment setup page
    /*
    // Only redirect if:
    // 1. User is logged in
    // 2. We have user data  
    // 3. Check is complete (not loading)
    // 4. We checked this user already
    // 5. Check has completed successfully OR (fallback) we're not loading and not checking
    // 6. MercadoPago is not connected
    // 7. No error occurred
    // 8. Not already on configuration page
    // 9. Not on login page (to avoid redirecting during login process)
    if (
      isLoggedIn && 
      user && 
      !isLoading && 
      lastCheckedUserIdRef.current === user.id &&
      (hasCompletedCheckRef.current || (!isLoading && !isCheckingRef.current)) &&
      !isConnected &&
      !error &&
      location.pathname !== '/configuracion' &&
      location.pathname !== '/login'
    ) {
      console.log('🔄 MercadoPago no conectado, redirigiendo a configuración...', {
        reason: 'MercadoPago not connected after verification',
        from: location.pathname,
        hasCompletedCheck: hasCompletedCheckRef.current,
        fallbackCondition: !isLoading && !isCheckingRef.current
      });
      
      // Small delay to ensure UI is stable
      setTimeout(() => {
        navigate('/configuracion', { 
          state: { autoRedirect: true } 
        });
      }, 300);
    } else if (isConnected && user && lastCheckedUserIdRef.current === user.id && (hasCompletedCheckRef.current || (!isLoading && !isCheckingRef.current))) {
      console.log('✅ MercadoPago ya conectado, no se requiere redirección');
    }
    */
  }, [isLoggedIn, user, isLoading, isConnected, error, location.pathname, navigate]);

  // Reset when user logs out
  useEffect(() => {
    if (!isLoggedIn) {
      console.log('🔄 Usuario deslogueado, reseteando estado de MercadoPago check');
      lastCheckedUserIdRef.current = null;
      isCheckingRef.current = false;
      hasCompletedCheckRef.current = false;
    }
  }, [isLoggedIn]);

  return {
    isLoading,
    isConnected,
    error,
    checkConnectionStatus,
  };
};
